import { useEffect, useRef, useState } from "react";
import Button from "@components/ui/Button";
import ErrorBanner from "@components/shared/ErrorBanner";
import {
  createVisitType, deactivateVisitType, fetchVisitTypes, updateVisitType,
} from "@services/clinicProviders";

const ROLE_OPTIONS = [
  { value: "hygienist", label: "Hygienist" },
  { value: "dentist", label: "Dentist" },
];

const LENGTH_BADGE = {
  learned: ["Length learned", "bg-gray-100 text-gray-700"],
  manual: ["Length set by staff", "bg-blue-100 text-blue-800"],
  default: ["Default length", "bg-amber-50 text-amber-800"],
};

const inputClass =
  "border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

const splitPhrases = (text) => text.split(",").map((p) => p.trim()).filter(Boolean);

/**
 * The clinic's visit types: what a caller's reason maps to, how long each
 * takes, who does it. Five defaults are seeded on first read; the length
 * is learned from the clinic's history unless staff set it. The flags
 * steer the connected inbound flow: urgent visits take the earliest slot
 * with anyone eligible, the new-patient type is what first visits book,
 * the default dentist type is where an unclear reason lands.
 *
 * Every change saves at once; the drawer's Save button is not involved.
 */
export default function VisitTypesEditor({ clinicId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("hygienist");
  const [newLength, setNewLength] = useState(30);
  const clinicRef = useRef(clinicId);

  useEffect(() => {
    let cancelled = false;
    clinicRef.current = clinicId;
    setLoading(true);
    setLoadError(null);
    setSaveError(null);
    setRows([]);
    if (!clinicId) { setLoading(false); return undefined; }
    (async () => {
      try {
        const list = await fetchVisitTypes(clinicId);
        if (!cancelled) setRows(list);
      } catch (e) {
        if (!cancelled) setLoadError(e.message || "Could not load the clinic's visit types");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clinicId]);

  const replaceRow = (updated) =>
    setRows((list) => list.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));

  // One write for the clinic on screen; the reply is dropped if the admin
  // switched clinics meanwhile.
  const run = async (write, apply) => {
    const forClinic = clinicId;
    setBusy(true);
    setSaveError(null);
    try {
      const result = await write();
      if (clinicRef.current !== forClinic) return;
      apply(result);
    } catch (e) {
      if (clinicRef.current !== forClinic) return;
      setSaveError(e.message || "Could not save the change");
    } finally {
      setBusy(false);
    }
  };

  const update = (vt, fields) =>
    run(
      () => updateVisitType({ clinicId, visitTypeId: vt.id, fields }),
      (updated) => { if (updated) replaceRow(updated); },
    );

  const deactivate = (vt) =>
    run(
      () => deactivateVisitType({ clinicId, visitTypeId: vt.id }),
      () => setRows((list) => list.filter((r) => r.id !== vt.id)),
    );

  const add = () => {
    const name = newName.trim();
    if (!name) return;
    run(
      () => createVisitType({
        clinicId, fields: { name, default_role: newRole, length_minutes: Number(newLength) || 30, phrases: [] },
      }),
      (created) => { if (created) { setRows((list) => [...list, created]); setNewName(""); } },
    );
  };

  return (
    <div className="border rounded-lg p-4 bg-white space-y-3">
      <div>
        <div className="text-sm font-semibold">Visit types</div>
        <p className="text-xs text-gray-500">
          What a caller's reason maps to, how long it takes and who does it. Phrases are what callers
          say, comma-separated. Lengths are learned from the clinic's history unless set here. Urgent
          visits take the earliest time with anyone who can see them; the new-patient type is what a
          first visit books; the default dentist type is where an unclear reason lands.
        </p>
      </div>
      <ErrorBanner message={loadError} onDismiss={() => setLoadError(null)} />
      <ErrorBanner message={saveError} onDismiss={() => setSaveError(null)} />
      {loading && <p className="text-sm text-gray-500">Loading visit types…</p>}
      {!loading && !loadError && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500">
                <th className="py-1 pr-2">Name</th>
                <th className="py-1 pr-2">Phrases</th>
                <th className="py-1 pr-2">Length (min)</th>
                <th className="py-1 pr-2">Flags</th>
                <th className="py-1 pr-2">Role</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((vt) => (
                <VisitTypeRow key={vt.id} vt={vt} busy={busy} onUpdate={update} onDeactivate={deactivate} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && !loadError && (
        <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-gray-200">
          <input type="text" className={inputClass} value={newName} placeholder="New visit type"
                 aria-label="New visit type name" disabled={busy} onChange={(e) => setNewName(e.target.value)} />
          <select className={inputClass} value={newRole} aria-label="New visit type role" disabled={busy}
                  onChange={(e) => setNewRole(e.target.value)}>
            {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <input type="number" min="5" max="480" className={`${inputClass} w-24`} value={newLength}
                 aria-label="New visit type length" disabled={busy} onChange={(e) => setNewLength(e.target.value)} />
          <Button color="light" size="xs" disabled={busy || !newName.trim()} onClick={add}>Add visit type</Button>
        </div>
      )}
    </div>
  );
}

function VisitTypeRow({ vt, busy, onUpdate, onDeactivate }) {
  const [badgeText, badgeClass] = LENGTH_BADGE[vt.length_source] || LENGTH_BADGE.default;
  const phrasesText = (vt.phrases || []).join(", ");

  const blurName = (e) => {
    const name = e.target.value.trim();
    if (name && name !== vt.name) onUpdate(vt, { name });
  };
  const blurPhrases = (e) => {
    const phrases = splitPhrases(e.target.value);
    if (JSON.stringify(phrases) !== JSON.stringify(vt.phrases || [])) onUpdate(vt, { phrases });
  };
  const blurLength = (e) => {
    const minutes = parseInt(e.target.value, 10);
    if (Number.isFinite(minutes) && minutes !== vt.length_minutes) onUpdate(vt, { length_minutes: minutes });
  };

  return (
    <tr>
      <td className="py-2 pr-2 align-top">
        <input key={vt.name} type="text" className={inputClass} defaultValue={vt.name} disabled={busy}
               aria-label={`Name of ${vt.name}`} onBlur={blurName} />
      </td>
      <td className="py-2 pr-2 align-top">
        <input key={phrasesText} type="text" className={`${inputClass} w-full`} defaultValue={phrasesText}
               disabled={busy} aria-label={`Phrases for ${vt.name}`} onBlur={blurPhrases} />
      </td>
      <td className="py-2 pr-2 align-top">
        <div className="flex flex-wrap items-center gap-2">
          <input key={vt.length_minutes} type="number" min="5" max="480" className={`${inputClass} w-20`}
                 defaultValue={vt.length_minutes} disabled={busy} aria-label={`Length for ${vt.name}`}
                 onBlur={blurLength} />
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass}`}>{badgeText}</span>
          {vt.length_source === "manual" && (
            <Button color="light" size="xs" disabled={busy} onClick={() => onUpdate(vt, { reset_length: true })}
                    aria-label={`Use learned length for ${vt.name}`}>Use learned</Button>
          )}
        </div>
      </td>
      <td className="py-2 pr-2 align-top">
        <div className="flex flex-col gap-1 text-xs text-gray-700">
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={!!vt.is_urgent} disabled={busy} aria-label={`Urgent: ${vt.name}`}
                   onChange={() => onUpdate(vt, { is_urgent: !vt.is_urgent })} />
            Urgent
          </label>
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={!!vt.is_new_patient} disabled={busy}
                   aria-label={`New patient: ${vt.name}`}
                   onChange={() => onUpdate(vt, { is_new_patient: !vt.is_new_patient })} />
            New patient
          </label>
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={!!vt.is_default_dentist} disabled={busy}
                   aria-label={`Default dentist: ${vt.name}`}
                   onChange={() => onUpdate(vt, { is_default_dentist: !vt.is_default_dentist })} />
            Default dentist
          </label>
        </div>
      </td>
      <td className="py-2 pr-2 align-top">
        <select className={inputClass} value={vt.default_role || "dentist"} disabled={busy}
                aria-label={`Role for ${vt.name}`} onChange={(e) => onUpdate(vt, { default_role: e.target.value })}>
          {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </td>
      <td className="py-2 align-top">
        <Button color="light" size="xs" disabled={busy} onClick={() => onDeactivate(vt)}
                aria-label={`Deactivate ${vt.name}`}>Deactivate</Button>
      </td>
    </tr>
  );
}
